import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from "../../services/auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { EstimatesService, EstimateVersionCreate } from "../../services/estimates.service";
import { debounce, distinctUntilChanged, map, pairwise, shareReplay, startWith, switchMap, take } from "rxjs/operators";
import { BehaviorSubject, combineLatest, Subscription, timer } from "rxjs";
import { Estimate, EstimateVersion } from "../../models/estimate.model";
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import { Client } from "projects/common/src/lib/models";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { ConfirmationDialog } from "projects/common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { UsersService } from "../../services/users.service";
import { BusinessService } from "../../services/business.service";
import { UtilsService } from 'projects/common/src/public-api';
import { ChatService, unseenIndicatorDebounceMs } from '../../services/chat.service';
import { ChatComponent } from "../../components/chat/chat.component";
import { NavigationService } from '../../services/navigation.service';
import { ViewAsService } from '../../services/view-as.service';
import { OnboardingService } from "../../services/onboarding.service";

const tabs = ['Estimate', 'Notes', 'Chat'] as const;
const sideTabs = tabs.slice(1);
type MainTab = typeof tabs[number];
type SideTab = Exclude<MainTab, 'Estimate'>;

const statusToTabMap: Record<string, string> = {
  'canceled': 'canceled',
  'canceledFromPayment': 'canceled',
  'declined': 'declined',
  'created': 'drafts',
  'sent': 'pending',
  'accepted': 'won',
};


// noinspection ES6BindWithArrowFunction
@Component({
  templateUrl: './estimate.component.html',
  styleUrls: ['./estimate.component.scss']
})
export class EstimateComponent implements OnInit, OnDestroy {

  @ViewChild('chat') chat!: ChatComponent;

  paramMapSub?: Subscription;

  readonly$ = this.viewAsService.readonly$;

  businessId$ = this.businessService.selectedBusiness$.pipe(
      map(business => business!.businessId)
  );

  currentUser$ = this.usersService.currentUser$;

  differentInFocus$ = new BehaviorSubject<boolean>(false);
  currentEstimateVersionItemsLength$ = new BehaviorSubject(0);
  user$ = this.authService.user$;
  currentEstimateVersionId$ = new BehaviorSubject(1);
  scrollPosition: number = 0;
  preview: boolean = false;
  sendLoading: boolean = false;
  actions = ['New', 'Duplicate'];
  addedNewVersion = false;

  params$ = this.route.params.pipe(
    distinctUntilChanged((prev, cur) => prev?.estimateId && cur.estimateId && prev.estimateId !== cur.estimateId),
    map(params => {
      return {
        estimateId: +params['estimateId'],
        workflowId: +params['workflowId']
      }
    })
  );

  estimate$ = this.params$.pipe(
    switchMap(params => {
      return this.estimatesService.estimateObservable(params.estimateId, params.workflowId).pipe(
        startWith(null),
        distinctUntilChanged(),
        pairwise(),
        map(([prevEstimate, currEstimate], index) => {
          if (!currEstimate)
            return null;

          if (!this.navigationService.fromReports) {
            const prevActiveVersionId = prevEstimate?.activeVersionId;
            const currActiveVersionId = currEstimate.activeVersionId;
            const prevVersionStatus = prevEstimate?.versions.find(version => version.id === prevActiveVersionId)?.status;
            const currVersionStatus = currEstimate?.versions.find(version => version.id === currActiveVersionId)?.status;
            if (prevVersionStatus && currVersionStatus !== prevVersionStatus
              || prevActiveVersionId === null && currActiveVersionId
              || prevActiveVersionId && prevActiveVersionId !== currActiveVersionId
            )
              this.navigationService.setBackRoute(`/lobby/estimates/${statusToTabMap[currVersionStatus ?? '']}`);
          }

          if (index === 0)
            this.currentEstimateVersionId$.next(this.estimatesService.findHighestDominantVersionId(currEstimate!.versions));

          return currEstimate;
        })
      );
    }),
    shareReplay(1),
  );

  estimateVersions$ = this.estimate$.pipe(
      map(estimate => estimate!.versions),
      startWith([] as EstimateVersion[]),
      pairwise(),
      map(([_, versions]) => versions)
  );

  currentEstimateVersion$ = this.currentEstimateVersionId$.pipe(
    switchMap(currentEstimateVersionId => {
      return this.estimateVersions$.pipe(
        map(versions => {
          if (this.addedNewVersion) {
            this.addedNewVersion = false;
            const id = this.estimatesService.findHighestVersionId(versions);
            this.currentEstimateVersionId$.next(id);
          }
          const currentEstimateVersion = versions.find(estimateVersion => estimateVersion.id === currentEstimateVersionId);
          if (currentEstimateVersion?.items)
            this.currentEstimateVersionItemsLength$.next(currentEstimateVersion!.items.length);

          return currentEstimateVersion;
        })
      );
    })
  );
  
  backButtonCaption$ = this.estimate$.pipe(
    map(estimate => this.navigationService.fromReports || !estimate?.activeVersionId ? 'Back' : 'Close')
  );

  largeScreen$ = this.utilsService.onScreenBreakpointChange('lg');
  selectedTabSubject = new BehaviorSubject<MainTab>('Estimate');
  selectedSideTabSubject = new BehaviorSubject<SideTab>('Notes');
  selectedTab$ = combineLatest([this.largeScreen$, this.selectedTabSubject, this.selectedSideTabSubject]).pipe(
    map(([largeScreen, mainTab, sideTab]) => largeScreen ? sideTab : mainTab)
  );
  
  showInfo$ = combineLatest([this.largeScreen$, this.selectedTab$]).pipe(
    map(([largeScreen, selectedTab]) => {
      return largeScreen || selectedTab === 'Estimate';
    })
  );

  showNotes$ = this.selectedTab$.pipe(
    map((selectedTab) => selectedTab === 'Notes')
  );

  showChat$ = this.selectedTab$.pipe(
    map((selectedTab) => selectedTab === 'Chat')
  );

  tabs$ = this.largeScreen$.pipe(
    map(largeScreen => (largeScreen ? sideTabs : tabs) as string[])
  );

  chat$ = this.params$.pipe(
    map(params => params.workflowId),
    switchMap(workflowId => this.chatService.chatObservable(workflowId, true)),
    shareReplay(1)
  );

  tabsRedDots$ = combineLatest([
    this.chat$.pipe(
      map(chat => !!chat?.hasUnseenMessages),
      startWith(false),
      debounce(hasUnseen => timer(hasUnseen ? unseenIndicatorDebounceMs : 0))
    ),
    this.tabs$
  ]).pipe(map(([chatHasUnseenMessages, tabs]) => tabs.map(tab => tab === 'Chat' && chatHasUnseenMessages)));

  async setActiveTab(tab: string) {
    const largeScreen = await this.largeScreen$.pipe(take(1)).toPromise();
    largeScreen ? this.selectedSideTabSubject.next(tab as SideTab) : this.selectedTabSubject.next(tab as MainTab);
  }

  clientUpdateFunction = async (currentEstimateVersionId: number, form: Client): Promise<any> => {
    await this.estimatesService.updateEstimateVersionClient(
      currentEstimateVersionId,
      form
    )
  }

  nuveiInfo$ = this.onboardingService.onboardingData$;
  showNuveiNotConnectedBanner$ = this.nuveiInfo$.pipe(
      map(nuveiInfo => {
        console.log(nuveiInfo);
        return nuveiInfo?.applicationStatus !== 'approved';
      })
  );

  constructor(
    private authService: AuthService,
    private estimatesService: EstimatesService,
    private businessService: BusinessService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private modalsService: ModalsService,
    private usersService: UsersService,
    private changeDetector: ChangeDetectorRef,
    private utilsService: UtilsService,
    private chatService: ChatService,
    private navigationService: NavigationService,
    private viewAsService: ViewAsService,
    private onboardingService: OnboardingService
  ) {}

  async ngOnInit() {
    await this.navigationService.setStateQueryParam();
    this.paramMapSub = this.route.paramMap
      .pipe(pairwise())
      .subscribe(([paramsPrev, paramsCur]) => {
        const idPrev = paramsPrev.get('estimateId');
        const idCur = paramsCur.get('estimateId');
        if (idPrev && idCur && idPrev !== idCur)
          location.reload();
      });
  }

  async ngOnDestroy() {
    const modalComponent = await this.modalsService.modalComponent$.pipe(take(1)).toPromise();
    if (!modalComponent)
      this.params$.pipe(take(1)).toPromise()
        .then(params => this.estimatesService.cleanUpEstimate(params.estimateId));
    this.paramMapSub?.unsubscribe();
  }

  async back() {
    const estimate = await this.estimate$.pipe(take(1)).toPromise();
    if (!estimate?.activeVersionId) {
      history.back();
      return;
    }
    this.navigationService.goBack();
  }

  async handleVersionPickerAction(action: typeof this.actions[number]) {
    switch (action) {
      case 'New':
        await this.createEstimateVersion();
        break;
      case 'Duplicate':
        await this.duplicateEstimateVersion();
        break;
      default:
        break;
    }
  }

  setCurrentVersion(estimateVersion: EstimateVersion) {
    this.preview = false;
    this.currentEstimateVersionId$.next(estimateVersion.id);
    this.currentEstimateVersionItemsLength$.next(estimateVersion.items?.length ?? 0);
  }

  async createEstimateVersion() {
    const estimate = await this.estimate$.pipe(take(1)).toPromise();
    const currentEstimateVersionId = await this.currentEstimateVersionId$.pipe(take(1)).toPromise();
    try {
      const user = await this.usersService.currentUser$.pipe(take(1)).toPromise();
      const estimateVersion = estimate!.versions.find(version => version.id === currentEstimateVersionId)!
      const data: EstimateVersionCreate = {
        inClient: {
          address: estimateVersion.clientAddress,
          businessName: estimateVersion.clientBusinessName,
          email: estimateVersion.clientEmail,
          extNumber: estimateVersion.clientExtNumber,
          firstName: estimateVersion.clientFirstName,
          lastName: estimateVersion.clientLastName,
          phoneNumber: estimateVersion.clientPhoneNumber,
          salesTaxPercentage: estimateVersion.clientSalesTaxPercentage!,
          type: estimateVersion.clientType,
          unit: estimateVersion.clientUnit,
        },
        inCreatedBy: user.id,
        inEstimateId: estimate!.id,
        inWorkflowId: estimate!.workflowId,
      };
      await this.estimatesService.createEstimateVersion(data);
      this.addedNewVersion = true;
      showSnackbar(this.snackBar, {
        message: "Estimate created",
        duration: 2000,
      });
    } catch (e) {
      console.log(e)
    }
  }

  async duplicateEstimateVersion() {
    const estimate = await this.estimate$.pipe(take(1)).toPromise();
    const currentEstimateVersion = await this.currentEstimateVersion$.pipe(take(1)).toPromise();
    await this.estimatesService.duplicateEstimateVersion(estimate!, currentEstimateVersion!);
    this.addedNewVersion = true;
    showSnackbar(this.snackBar, {
      message: "Estimate duplicated",
      duration: 2000,
    });
  }

  async sendEstimateVersion(
    estimate: Estimate,
    currentEstimateVersion: EstimateVersion
  ) {
    this.sendLoading = true;

    // Check if client has an email address
    if (!currentEstimateVersion.clientEmail || currentEstimateVersion.clientEmail.trim() === '') {
      showSnackbar(this.snackBar, {
        message: "Cannot send estimate: client email is required",
        duration: 3000,
      });
      this.sendLoading = false;
      return;
    }

    let sent = false;

    estimate.versions.forEach(version => {
      if (version.status === 'sent' || version.status === 'accepted')
        sent = true;
    });

    if (!sent) {
      await this.estimatesService.sendEstimate(currentEstimateVersion.id);
      this.preview = false;
      showSnackbar(this.snackBar, {
        message: "Estimate sent",
        duration: 2000,
      });
    } else {
      await this.modalsService.open(ConfirmationDialog, {
        behavior: ModalBehavior.Dialog,
        disableClose: true,
        data: {
          title: 'New Version',
          message: 'By sending a new version, you are cancelling the last version. The payment balance will appear in the new estimate. Are you sure you want to proceed?',
          actionTitle: 'Yes',
          actionColor: 'warn',
          cancelTitle: 'No',
          action: async () => {
            await this.estimatesService.sendEstimate(currentEstimateVersion.id);
            this.preview = false;
            showSnackbar(this.snackBar, {
              message: "Estimate sent",
              duration: 2000,
            });
          }
        }
      });
    }
    this.sendLoading = false;
  }

  sidePanelClick(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.chat?.onMenuClick(event);
  }

  detectChanges() {
    this.changeDetector.detectChanges();
  }
}
